import { Reorder, useDragControls } from "framer-motion";
import { FC, FormEvent, ReactNode, useState } from "react";
import { FaHeading, FaRegImage, FaYoutube } from "react-icons/fa";
import { ImParagraphLeft } from "react-icons/im";
import { IoCloseOutline } from "react-icons/io5";
import { Content, LocalizedText, News } from "../../services/generated/client";
import styles from "./styles.module.css";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { FileUploader } from "../FileUploader";

interface NewsFormProps {
  isAdding: boolean;
  news: News;
  onSubmit: (news: News) => void;
  isView: boolean;
  language: keyof LocalizedText;
}

const IconsPerType: Record<Content["type"], ReactNode> = {
  header: <FaHeading />,
  paragraph: <ImParagraphLeft />,
  image: <FaRegImage />,
  video: <FaYoutube />,
};

const shortId = () => Math.random().toString(36).slice(-4);

const addIds = (items: Array<Content>) => {
  return items.map((item) => {
    return { ...item, id: shortId() };
  });
};

const removeIds = (items: Array<Content & { id: string }>) => {
  return items.map((item) => {
    const { id, ...rest } = item;
    return rest;
  });
};

const NewsForm: FC<NewsFormProps> = ({
  isAdding,
  news,
  onSubmit,
  isView,
  language,
}) => {
  const controls = useDragControls();

  const [content, setContent] = useState(
    addIds(
      news.content.map((item) => ({
        ...item,
        content: item.content || localizedTextDefaultValue,
      }))
    )
  );
  const [title, setTitle] = useState<LocalizedText>(
    news.title || localizedTextDefaultValue
  );
  const [date, setDate] = useState(news.date || "");
  const [mainImageUrl, setMainImageUrl] = useState(news.mainImageUrl || "");

  const handleContentChange = (index: number, value: string) => {
    const updatedContent = [...content];
    const currentItem = updatedContent[index];

    if (currentItem.type === "image" || currentItem.type === "video") {
      updatedContent[index].content = {
        en: value,
        ka: value,
      };
    } else {
      updatedContent[index].content = {
        ...currentItem.content,
        [language]: value,
      };
    }

    setContent(updatedContent);
  };

  const handleAddContent = (
    type: "image" | "video" | "header" | "paragraph"
  ) => {
    setContent([
      ...content,
      { id: shortId(), type, content: localizedTextDefaultValue },
    ]);
  };

  const handleRemoveContent = (index: number) => {
    const updatedContent = content.filter((_, i) => i !== index);
    setContent(updatedContent);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    const cleanedContent = removeIds(content);

    onSubmit({
      ...news,
      date,
      title,
      mainImageUrl,
      content: cleanedContent,
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          {isView ? "News" : isAdding ? "Add News" : "Edit News"}
        </h2>
        <form onSubmit={handleFormSubmit} className={styles.form}>
          <div className={styles.formFields}>
            <label className={styles.label} htmlFor="title">
              Title
            </label>
            <input
              className={styles.input}
              id="title"
              type="text"
              value={title[language]}
              onChange={(e) =>
                setTitle({
                  ...title,
                  [language]: e.target.value,
                })
              }
              disabled={isView}
            />
          </div>
          <div className={styles.formFields}>
            <label className={styles.label} htmlFor="date">
              Date
            </label>
            <input
              className={styles.input}
              id="date"
              type="text"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isView}
            />
          </div>
          <div className={styles.formFields}>
            <label className={styles.label} htmlFor="mainImageUrl">
              Main Image
            </label>
            <FileUploader
              value={mainImageUrl}
              onUploaded={(url: string) => setMainImageUrl(url)}
              onDeleted={() => setMainImageUrl("")}
              isView={isView}
              path="News"
            />
          </div>

          <div className={styles.dynamicArray}>
            <Reorder.Group
              axis="y"
              values={content}
              onReorder={!isView ? setContent : () => {}}
              className={styles.ReorderGroup}
            >
              {content.map((item, index) => (
                <Reorder.Item
                  key={item.id}
                  value={item}
                  dragControls={controls}
                >
                  <div className={styles.group}>
                    {item.type === "paragraph" ? (
                      <>
                        <label className={styles.label}>
                          <ImParagraphLeft />
                        </label>
                        <textarea
                          className={styles.textareaContent}
                          placeholder={`enter ${item.type} content ${language}`}
                          value={item.content[language]}
                          onChange={(e) =>
                            handleContentChange(index, e.target.value)
                          }
                          disabled={isView}
                        />
                      </>
                    ) : (
                      <>
                        <label className={styles.label}>
                          {IconsPerType[item.type]}
                        </label>
                        {item.type === "image" ? (
                          <FileUploader
                            value={item.content[language]}
                            onUploaded={(url: string) =>
                              handleContentChange(index, url)
                            }
                            onDeleted={() => handleContentChange(index, "")}
                            isView={isView}
                            path="News"
                          />
                        ) : (
                          <input
                            className={styles.inputContent}
                            type="text"
                            placeholder={`enter ${item.type} content ${language}`}
                            value={item.content[language]}
                            onChange={(e) =>
                              handleContentChange(index, e.target.value)
                            }
                            disabled={isView}
                          />
                        )}
                      </>
                    )}
                    {!isView && (
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => handleRemoveContent(index)}
                      >
                        <IoCloseOutline />
                      </button>
                    )}
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {!isView && (
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => handleAddContent("image")}
                >
                  + Add Image
                </button>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => handleAddContent("video")}
                >
                  + Add Video
                </button>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => handleAddContent("header")}
                >
                  + Add Header
                </button>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => handleAddContent("paragraph")}
                >
                  + Add Paragraph
                </button>
              </div>
            )}
          </div>
          {!isView && (
            <button className={styles.primaryButton} type="submit">
              Save News
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewsForm;
